import React, { Fragment, useContext, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TokenizedCardDetails } from 'api/NPayments/Types/TokenizedCardDetails'
import ClientAreaAuditLogModal from 'components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { Ellipsis } from 'lucide-react'

import { useGridState } from '../../../../../features/DataGrid'
import { useApiClient } from '../../../../../utils/api/ApiClient'
import { NPaymentsApi } from '../../../../../utils/api/nPaymentsApi'
import { useCallbackWithForceRefresh } from '../../../../../utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '../../../../../utils/api/useFetch'
import { AuthSessionContext } from '../../../../../utils/AuthContext'
import { formatLocalDateUTC } from '../../../../../utils/date'
import { hasPermission } from '../../../../../utils/permissionsValidator'
import { EditCardholderNameForm } from './EditCardholderNameForm'
import { EditCardStatusForm } from './EditCardStatusForm'
import {
  formatCardExpirationDate,
  formatMaskedCardNumber,
  getTokenizedCardStatusName,
} from './tokenizedCardDetailsUtils'
import { TokenizedCardsDropdownMenu } from './TokenizedCardsDropdownMenu'

import styles from '../ClientDetailsDocument/ClientDetailsDocument.module.scss'

interface Props {
  clientId: string
}

const useClientDetailsTokenizedCardsFetch = (clientId: string) => {
  const paymentsApi = useApiClient(NPaymentsApi)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(() => {
    return paymentsApi.getClientTokenizedCards(clientId)
  }, [clientId])
  const { data: tokenizedCards, isLoading: isTokenizedCardsLoading } = useFetchOne(fetchData)

  const { callback: statusesCallback } = useCallbackWithForceRefresh(
    () => paymentsApi.getCardDetailsStatuses(),
    []
  )
  const { data: cardStatuses, isLoading: isCardStatusesLoading } = useFetchOne(statusesCallback)

  return {
    tokenizedCards,
    cardStatuses,
    isLoading: isTokenizedCardsLoading || isCardStatusesLoading,
    forceRefresh,
  }
}

export const ClientDetailsTokenizedCardsTab: React.FC<Props> = ({ clientId }) => {
  const paymentsApi = useApiClient(NPaymentsApi)

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const canEditTokenizedCardDetails = hasPermission(
    permissions,
    PermissionsEnum.CanEditTokenizedCardDetails
  )
  const canBrowseTokenizedCardDetails = hasPermission(
    permissions,
    PermissionsEnum.CanBrowseTokenizedCardDetails
  )

  const { gridState } = useGridState()
  const tokenizedCardsFetch = useClientDetailsTokenizedCardsFetch(clientId)
  const { tokenizedCards, cardStatuses, isLoading, forceRefresh } = tokenizedCardsFetch

  const [auditLogVisible, setAuditLogVisible] = useState<string | null>(null)
  const [editCardholderNameModal, setEditCardholderNameModal] =
    useState<TokenizedCardDetails | null>(null)
  const [editStatusModal, setEditStatusModal] = useState<TokenizedCardDetails | null>(null)

  const handleCardholderNameSubmit = async (values: {
    cardHolderName: string
    comment: string
  }) => {
    if (!editCardholderNameModal) {
      return
    }

    await paymentsApi.updateCardDetailsCardholderName(editCardholderNameModal.id, values)
    setEditCardholderNameModal(null)
    setToast(successToast('Cardholder name updated successfully.'))
    forceRefresh()
  }

  const handleCardStatusSubmit = async (values: { statusId: number; comment: string }) => {
    if (!editStatusModal) {
      return
    }

    await paymentsApi.updateCardDetailsStatus(editStatusModal.id, values)
    setEditStatusModal(null)
    setToast(successToast('Card status updated successfully.'))
    forceRefresh()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <Fragment>
      {editCardholderNameModal && (
        <Modal visible={!!editCardholderNameModal} onClose={() => setEditCardholderNameModal(null)}>
          <EditCardholderNameForm
            cardholderName={editCardholderNameModal.cardholderName}
            onSubmit={handleCardholderNameSubmit}
            onClose={() => setEditCardholderNameModal(null)}
          />
        </Modal>
      )}
      {editStatusModal && (
        <Modal visible={!!editStatusModal} onClose={() => setEditStatusModal(null)}>
          <EditCardStatusForm
            currentStatusId={editStatusModal.status.id}
            statuses={cardStatuses || []}
            onSubmit={handleCardStatusSubmit}
            onClose={() => setEditStatusModal(null)}
          />
        </Modal>
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={!!auditLogVisible}
          onClose={() => setAuditLogVisible(null)}
          cardDetailsId={auditLogVisible}
        />
      )}

      <Grid
        {...gridState}
        resizable
        selectedField='selected'
        expandField='expanded'
        data={tokenizedCards || []}
        sort={gridState.sort}
        total={tokenizedCards?.length || 0}
        take={100}
        skip={0}
        style={{ zIndex: 0 }}
      >
        <Column
          width={180}
          minResizableWidth={180}
          field='tokenId'
          title='Token ID'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={180}
          minResizableWidth={180}
          field='tokenCreatedDate'
          title='Token Creation Date & Time'
          cell={(gridCellProps: GridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.tokenCreatedDate, true)}</td>
          )}
        />
        <Column
          width={180}
          minResizableWidth={180}
          field='paymentMethod.name'
          title='PSP'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={160}
          minResizableWidth={160}
          field='maskedCardNumber'
          title='Card Number'
          cell={(gridCellProps: GridCellProps) => (
            <td>{formatMaskedCardNumber(gridCellProps.dataItem.maskedCardNumber)}</td>
          )}
        />
        <Column
          width={170}
          minResizableWidth={170}
          field='cardholderName'
          title='Cardholder Name'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={120}
          minResizableWidth={120}
          field='expirationDate'
          title='Expiration'
          cell={(gridCellProps: GridCellProps) => (
            <td>{formatCardExpirationDate(gridCellProps.dataItem.expirationDate)}</td>
          )}
        />
        <Column
          width={150}
          minResizableWidth={150}
          field='status.name'
          title='Status'
          cell={(gridCellProps: GridCellProps) => (
            <td>{getTokenizedCardStatusName(gridCellProps.dataItem.status)}</td>
          )}
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          width={220}
          minResizableWidth={220}
          field='comment'
          title='Comment'
          className='table-cell-nonLatin-ellipsis'
        />
        <Column
          title=''
          width={50}
          locked
          minResizableWidth={50}
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__sticky'>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <button
                      type='button'
                      className={styles.table_menu_button}
                      aria-label='More options'
                    />
                  }
                >
                  <Ellipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <TokenizedCardsDropdownMenu
                    canBrowseTokenizedCardDetails={canBrowseTokenizedCardDetails}
                    canEditTokenizedCardDetails={canEditTokenizedCardDetails}
                    dataItem={gridCellProps.dataItem}
                    onEditCardholderNameModal={setEditCardholderNameModal}
                    onEditStatusModal={setEditStatusModal}
                    onAuditModalOpen={(currentCard) => setAuditLogVisible(currentCard.id)}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          )}
        />
      </Grid>
    </Fragment>
  )
}
